import { useEffect, useState } from 'react';

import type { Body, Meta, Uppy } from '@uppy/core';

import { useUppy } from '#src/app/shared/hooks/uppy';

type Result = ModelHookResult<
	{
		isLoading: boolean;
		uppy: Uppy<Meta, Body>;
	},
	{
		removeImage: () => void;
	}
>;

// eslint-disable-next-line max-lines-per-function
export function useImageUploaderUppyWrapper({ onChange }: { onChange: (value: string) => void }): Result {
	const [isLoading, setIsLoading] = useState<boolean>(false);

	const {
		data: { uppy },
	} = useUppy({
		maxNumberOfFiles: 1,
		maxFileSize: 5 * 1024 * 1024, // 5MB
		imageLocation: 'COMMS_STACK_UPLOADS',
		autoProceed: true,
	});

	useEffect(() => {
		const onFileAdded = () => {
			setIsLoading(true);
		};
		const onFileRemoved = () => {
			setIsLoading(false);
		};
		const onUploadError: Parameters<typeof uppy.on<'upload-error'>>[1] = (file, error) => {
			if (file) {
				uppy.removeFile(file.id);
			}
			// eslint-disable-next-line no-alert
			alert(error.message || 'Unknown error');
		};
		const onComplete: Parameters<typeof uppy.on<'complete'>>[1] = (response) => {
			if (response.successful?.[0]?.uploadURL) {
				onChange(response.successful[0].uploadURL);
				setIsLoading(false);
			} else {
				uppy.clear();
				setIsLoading(false);
				// eslint-disable-next-line no-alert
				alert('Image upload failed');
			}
		};
		const onRestrictionFailed: Parameters<typeof uppy.on<'restriction-failed'>>[1] = (file, error) => {
			if (file) {
				uppy.removeFile(file.id);
			}
			// eslint-disable-next-line no-alert
			alert(error.message);
		};

		uppy.on('file-added', onFileAdded);
		uppy.on('file-removed', onFileRemoved);
		uppy.on('upload-error', onUploadError);
		uppy.on('complete', onComplete);
		uppy.on('restriction-failed', onRestrictionFailed);

		return () => {
			uppy.off('file-added', onFileAdded);
			uppy.off('file-removed', onFileRemoved);
			uppy.off('upload-error', onUploadError);
			uppy.off('complete', onComplete);
			uppy.off('restriction-failed', onRestrictionFailed);
		};
	}, [onChange, uppy]);

	const removeImage = () => {
		// eslint-disable-next-line no-alert
		if (!window.confirm('Are you sure you want to delete this photo?')) {
			return;
		}
		onChange('');
		uppy.clear();
	};

	return {
		data: {
			isLoading,
			uppy,
		},
		actions: {
			removeImage,
		},
	};
}
